import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../../firebase';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import Alerts from '../../components/Alerts';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
}));

const ClickableTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const LocationMarker = ({ setLocation }) => {
  const [position, setPosition] = useState(null);

  useMapEvents({
    click(e) {
      setPosition(e.latlng);
      setLocation(e.latlng);
    },
  });

  return position === null ? null : <Marker position={position} />;
};

const AddCompany = ({ darkMode }) => {
  const [view, setView] = useState('viewCompanies');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [error, setError] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    companyType: '',
    companyName: '',
    unit: '',
    companyAddress: '',
    companyEmail: '',
    companyPhone: '',
    adminUserId: '',
    adminPassword: '',
    isPureVeg: '',
    companyCode: '',
    location: { lat: 0, lng: 0 }, // New location field
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterPureVeg, setFilterPureVeg] = useState('All');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'companies'));
        const companiesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCompanies(companiesData);
        applyFilters(companiesData, searchQuery, filterType, filterPureVeg);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Failed to load company data. Check Firestore permissions or data.');
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      companyCode: generateBusinessCode(prev.companyName, prev.unit),
    }));
  }, [formData.companyName, formData.unit]);

  const applyFilters = (items, query, type, pureVeg) => {
    let filtered = items;
    if (query) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (type !== 'All') {
      filtered = filtered.filter((item) => item.type === type);
    }
    if (pureVeg !== 'All') {
      filtered = filtered.filter((item) => item.isPureVeg === (pureVeg === 'Pure Veg'));
    }
    setFilteredCompanies(filtered);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(companies, query, filterType, filterPureVeg);
  };

  const handleFilterTypeChange = (e) => {
    const type = e.target.value;
    setFilterType(type);
    applyFilters(companies, searchQuery, type, filterPureVeg);
  };

  const handleFilterPureVegChange = (e) => {
    const pureVeg = e.target.value;
    setFilterPureVeg(pureVeg);
    applyFilters(companies, searchQuery, filterType, pureVeg);
  };

  const generateBusinessCode = (name, unit) => {
    if (!name) return '';
    const nameWords = name.toLowerCase().split(' ').filter(word => word.length > 0);
    let code = nameWords.length >= 2 ? nameWords.slice(0, 2).map(word => word[0]).join('') : nameWords[0].slice(0, 2);
    if (unit) {
      const unitCode = unit.toLowerCase().slice(0, 2);
      code += unitCode;
    }
    return code;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({
      ...prev,
      companyPhone: value,
    }));
  };

  const setLocation = (latlng) => {
    setFormData((prev) => ({
      ...prev,
      location: { lat: latlng.lat, lng: latlng.lng },
    }));
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const errors = {};
    if (!formData.companyName || /[\\/]/.test(formData.companyName)) {
      errors.companyName = 'Business name cannot be empty or contain "/" or "\\".';
    }
    if (!formData.unit) {
      errors.unit = 'Unit cannot be empty.';
    }
    if (formData.isPureVeg === '') {
      errors.isPureVeg = 'Please specify if the business is Pure Veg or Non-Veg.';
    }
    if (!formData.companyPhone || !/^\d{10}$/.test(formData.companyPhone)) {
      errors.companyPhone = 'Phone number must be exactly 10 digits (e.g., 1234567890).';
    }
    if (!formData.companyEmail || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.companyEmail)) {
      errors.companyEmail = 'Please enter a valid email address (e.g., example@domain.com).';
    }
    if (!formData.companyAddress) {
      errors.companyAddress = 'Address cannot be empty.';
    }
    if (!formData.adminUserId || /[\\/!]/.test(formData.adminUserId)) {
      errors.adminUserId = 'Admin User ID cannot be empty or contain "/" or "!".';
    }
    if (!formData.adminPassword) {
      errors.adminPassword = 'Password cannot be empty.';
    }
    if (!formData.companyType) {
      errors.companyType = 'Please select a business type.';
    }
    if (formData.companyCode) {
      const companyQuery = query(collection(db, 'companies'), where('id', '==', formData.companyCode));
      const querySnapshot = await getDocs(companyQuery);
      if (!querySnapshot.empty) {
        errors.companyCode = 'Business code already exists.';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const pseudoEmail = `${formData.companyCode}#${formData.adminUserId}@platterly.in`;
      const usersQuery = query(collection(db, 'users'), where('userId', '==', `${formData.companyCode}#${formData.adminUserId}`));
      const querySnapshot = await getDocs(usersQuery);
      if (!querySnapshot.empty) {
        setFormErrors({ adminUserId: 'Admin User ID already exists for this business.' });
        setLoading(false);
        return;
      }

      const companyDocRef = doc(db, 'companies', formData.companyCode);
      const companyDoc = await getDoc(companyDocRef);
      if (companyDoc.exists()) {
        setFormErrors({ companyCode: 'Business code already exists.' });
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, pseudoEmail, formData.adminPassword);
      const user = userCredential.user;

      await setDoc(companyDocRef, {
        name: formData.companyName,
        type: formData.companyType,
        unit: formData.unit,
        address: formData.companyAddress,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        isPureVeg: formData.isPureVeg === 'Pure Veg',
        location: formData.location, // Save location
        admin: { userId: `${formData.companyCode}#${formData.adminUserId}`, uid: user.uid },
        createdAt: new Date().toISOString(),
      });

      await setDoc(doc(db, 'users', user.uid), {
        userId: `${formData.companyCode}#${formData.adminUserId}`,
        role: 'admin',
        companyId: formData.companyCode,
        createdTime: new Date().toISOString(),
        uid: user.uid,
      });

      setSnackbar({
        open: true,
        message: 'Business and admin added successfully!',
        severity: 'success',
      });
      resetForm();
      setOpenAddDialog(false);
      querySnapshot = await getDocs(collection(db, 'companies'));
      const updatedCompanies = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCompanies(updatedCompanies);
      applyFilters(updatedCompanies, searchQuery, filterType, filterPureVeg);
    } catch (err) {
      console.error('Error adding business or admin:', err);
      setFormErrors({ general: err.message || 'Failed to add business or admin.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const errors = {};
    if (!formData.companyName || /[\\/]/.test(formData.companyName)) {
      errors.companyName = 'Business name cannot be empty or contain "/" or "\\".';
    }
    if (!formData.unit) {
      errors.unit = 'Unit cannot be empty.';
    }
    if (formData.isPureVeg === '') {
      errors.isPureVeg = 'Please specify if the business is Pure Veg or Non-Veg.';
    }
    if (!formData.companyPhone || !/^\d{10}$/.test(formData.companyPhone)) {
      errors.companyPhone = 'Phone number must be exactly 10 digits (e.g., 1234567890).';
    }
    if (!formData.companyEmail || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.companyEmail)) {
      errors.companyEmail = 'Please enter a valid email address (e.g., example@domain.com).';
    }
    if (!formData.companyAddress) {
      errors.companyAddress = 'Address cannot be empty.';
    }
    if (!formData.adminUserId || /[\\/!]/.test(formData.adminUserId)) {
      errors.adminUserId = 'Admin User ID cannot be empty or contain "/" or "!".';
    }
    if (!formData.companyType) {
      errors.companyType = 'Please select a business type.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const companyCode = selectedCompany ? selectedCompany.id : generateBusinessCode(formData.companyName, formData.unit);
      const pseudoEmail = `${companyCode}#${formData.adminUserId}@platterly.in`;
      let userUid = selectedCompany.admin.uid;
      if (selectedCompany && selectedCompany.admin.userId !== `${companyCode}#${formData.adminUserId}`) {
        const usersQuery = query(collection(db, 'users'), where('userId', '==', `${companyCode}#${formData.adminUserId}`));
        const querySnapshot = await getDocs(usersQuery);
        if (!querySnapshot.empty) {
          setFormErrors({ adminUserId: 'Admin User ID already exists for this business.' });
          setLoading(false);
          return;
        }
        const userCredential = await createUserWithEmailAndPassword(auth, pseudoEmail, formData.adminPassword);
        userUid = userCredential.user.uid;
      } else if (formData.adminPassword) {
        setFormErrors({ adminPassword: 'Password update requires Firebase Console.' });
        setLoading(false);
        return;
      }

      const companyDocRef = doc(db, 'companies', companyCode);
      await setDoc(companyDocRef, {
        name: formData.companyName,
        type: formData.companyType,
        unit: formData.unit,
        address: formData.companyAddress,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        isPureVeg: formData.isPureVeg === 'Pure Veg',
        location: formData.location, // Save location
        admin: { userId: `${companyCode}#${formData.adminUserId}`, uid: userUid },
        createdAt: selectedCompany ? selectedCompany.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      await setDoc(doc(db, 'users', userUid), {
        userId: `${companyCode}#${formData.adminUserId}`,
        role: 'admin',
        companyId: companyCode,
        createdTime: selectedCompany ? selectedCompany.createdAt : new Date().toISOString(),
        updatedTime: new Date().toISOString(),
        uid: userUid,
      });

      setSnackbar({
        open: true,
        message: 'Business and admin updated successfully! Password updates require Firebase Console.',
        severity: 'success',
      });
      resetForm();
      setOpenEditDialog(false);
      setSelectedCompany(null);
      const querySnapshot = await getDocs(collection(db, 'companies'));
      const updatedCompanies = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCompanies(updatedCompanies);
      applyFilters(updatedCompanies, searchQuery, filterType, filterPureVeg);
    } catch (err) {
      console.error('Error updating business or admin:', err);
      setFormErrors({ general: err.message || 'Failed to update business or admin.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'companies', itemToDelete.id));
      await deleteDoc(doc(db, 'users', itemToDelete.admin.uid));
      setSnackbar({
        open: true,
        message: 'Business and admin deleted successfully!',
        severity: 'success',
      });
      const querySnapshot = await getDocs(collection(db, 'companies'));
      const updatedCompanies = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCompanies(updatedCompanies);
      applyFilters(updatedCompanies, searchQuery, filterType, filterPureVeg);
      setOpenDeleteDialog(false);
      setItemToDelete(null);
      if (view === 'companyDetails') {
        setView('viewCompanies');
        setSelectedCompany(null);
      }
    } catch (err) {
      console.error('Error deleting business or admin:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete business or admin.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      companyType: '',
      companyName: '',
      unit: '',
      companyAddress: '',
      companyEmail: '',
      companyPhone: '',
      adminUserId: '',
      adminPassword: '',
      isPureVeg: '',
      companyCode: '',
      location: { lat: 0, lng: 0 },
    });
    setFormErrors({});
    setShowPassword(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openEditForm = (company) => {
    setSelectedCompany(company);
    setFormData({
      companyType: company.type,
      companyName: company.name,
      unit: company.unit || '',
      companyAddress: company.address,
      companyEmail: company.email,
      companyPhone: company.phone,
      adminUserId: company.admin.userId.split('#')[1],
      adminPassword: '',
      isPureVeg: company.isPureVeg ? 'Pure Veg' : 'Non-Veg',
      companyCode: company.id,
      location: company.location || { lat: 0, lng: 0 }, // Load existing location if available
    });
    setOpenEditDialog(true);
  };

  const renderContent = () => {
    if (view === 'viewCompanies') {
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Businesses List
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                resetForm();
                setOpenAddDialog(true);
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
            >
              Add Business
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={handleFilterTypeChange}
                label="Type"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="foodcourt">Food Court</MenuItem>
                <MenuItem value="hotel">Hotel</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Pure Veg</InputLabel>
              <Select
                value={filterPureVeg}
                onChange={handleFilterPureVegChange}
                label="Pure Veg"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pure Veg">Pure Veg</MenuItem>
                <MenuItem value="Non-Veg">Non-Veg</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Search by Name"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ flexGrow: 1, maxWidth: 300, borderRadius: '8px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {filteredCompanies.length > 0 ? (
            <StyledCard>
              <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Unit</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Pure Veg</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Phone</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompanies.map((company) => (
                      <ClickableTableRow
                        key={company.id}
                        onClick={() => {
                          setSelectedCompany(company);
                          setView('companyDetails');
                        }}
                      >
                        <TableCell sx={{ color: 'text.primary' }}>{company.name}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{company.type}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{company.unit || 'N/A'}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{company.isPureVeg ? 'Yes' : 'No'}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{company.email}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{company.phone}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              onClick={() => openEditForm(company)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setItemToDelete(company);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </ClickableTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          ) : (
            <Typography color="text.secondary">
              No businesses available. Add a business or adjust filters.
            </Typography>
          )}
        </Box>
      );
    }

    if (view === 'companyDetails' && selectedCompany) {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={() => setView('viewCompanies')}
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Business Details: {selectedCompany.name}
            </Typography>
          </Box>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2, color: 'text.primary' }}>
                Business Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Type:</strong> {selectedCompany.type}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {selectedCompany.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Unit:</strong> {selectedCompany.unit || 'N/A'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Pure Veg:</strong> {selectedCompany.isPureVeg ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Address:</strong> {selectedCompany.address}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> {selectedCompany.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Phone:</strong> {selectedCompany.phone}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Location:</strong> {`${selectedCompany.location.lat.toFixed(4)}, ${selectedCompany.location.lng.toFixed(4)}`}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Created At:</strong> {new Date(selectedCompany.createdAt).toLocaleString()}
              </Typography>
              {selectedCompany.updatedAt && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Updated At:</strong> {new Date(selectedCompany.updatedAt).toLocaleString()}
                </Typography>
              )}
              <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2, color: 'text.primary' }}>
                Admin Information
              </Typography>
              {selectedCompany.admin ? (
                <>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>User ID:</strong> {selectedCompany.admin.userId}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Created At:</strong> {new Date(selectedCompany.createdAt).toLocaleString()}
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">No admin assigned.</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => openEditForm(selectedCompany)}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Edit Business
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setItemToDelete(selectedCompany);
                    setOpenDeleteDialog(true);
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Delete Business
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      );
    }
  };

  const renderFormDialog = (isEdit = false) => (
    <Dialog
      open={isEdit ? openEditDialog : openAddDialog}
      onClose={() => {
        resetForm();
        if (isEdit) setOpenEditDialog(false);
        else setOpenAddDialog(false);
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {isEdit ? 'Edit Business' : 'Add Business'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {formErrors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {formErrors.general}
          </Alert>
        )}
        <form onSubmit={isEdit ? handleEditCompany : handleAddCompany}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Business Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              error={!!formErrors.companyName}
              helperText={formErrors.companyName || 'Enter your business name (e.g., Coastal Kitchen)'}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Unit"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              required
              error={!!formErrors.unit}
              helperText={formErrors.unit || 'Enter the unit or branch name (e.g., Unit 1, Main)'}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Business Code"
              name="companyCode"
              value={formData.companyCode}
              disabled
              helperText={formErrors.companyCode || 'Auto-generated from Business Name and Unit (e.g., ckunit1)'}
              sx={{ borderRadius: '8px' }}
            />
            <FormControl fullWidth required error={!!formErrors.companyType}>
              <InputLabel>Business Type</InputLabel>
              <Select
                name="companyType"
                value={formData.companyType}
                onChange={handleChange}
                label="Business Type"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="foodcourt">Food Court</MenuItem>
                <MenuItem value="hotel">Hotel</MenuItem>
              </Select>
              {formErrors.companyType && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.companyType}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth required error={!!formErrors.isPureVeg}>
              <InputLabel>Pure Veg or Non-Veg</InputLabel>
              <Select
                name="isPureVeg"
                value={formData.isPureVeg}
                onChange={handleChange}
                label="Pure Veg or Non-Veg"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="Pure Veg">Pure Veg</MenuItem>
                <MenuItem value="Non-Veg">Non-Veg</MenuItem>
              </Select>
              {formErrors.isPureVeg && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.isPureVeg}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Address"
              name="companyAddress"
              value={formData.companyAddress}
              onChange={handleChange}
              required
              multiline
              rows={3}
              error={!!formErrors.companyAddress}
              helperText={formErrors.companyAddress || ''}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Email"
              name="companyEmail"
              value={formData.companyEmail}
              onChange={handleChange}
              required
              type="email"
              error={!!formErrors.companyEmail}
              helperText={formErrors.companyEmail || ''}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="companyPhone"
              value={formData.companyPhone}
              onChange={handlePhoneInput}
              required
              type="text"
              inputProps={{ maxLength: 10 }}
              error={!!formErrors.companyPhone}
              helperText={formErrors.companyPhone || 'Enter exactly 10 digits (e.g., 1234567890)'}
              sx={{ borderRadius: '8px' }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={formData.companyCode ? `${formData.companyCode}#` : ''}
                label="Admin User ID Prefix"
                variant="outlined"
                disabled
                sx={{ width: '30%', borderRadius: '8px' }}
                helperText="Business Code Prefix"
              />
              <TextField
                fullWidth
                label="Admin User ID"
                name="adminUserId"
                value={formData.adminUserId}
                onChange={handleChange}
                required
                error={!!formErrors.adminUserId}
                helperText={formErrors.adminUserId || 'Enter the user ID without the prefix'}
                sx={{ borderRadius: '8px' }}
              />
            </Box>
            <TextField
              fullWidth
              label="Password"
              name="adminPassword"
              value={formData.adminPassword}
              onChange={handleChange}
              required={!isEdit}
              type={showPassword ? 'text' : 'password'}
              error={!!formErrors.adminPassword}
              helperText={formErrors.adminPassword || (isEdit ? 'Leave blank to keep existing password' : '')}
              sx={{ borderRadius: '8px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ height: 300, mt: 2 }}>
              <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <LocationMarker setLocation={setLocation} />
              </MapContainer>
            </Box>
            <TextField
              fullWidth
              label="Location"
              value={`${formData.location.lat.toFixed(4)}, ${formData.location.lng.toFixed(4)}`}
              disabled
              helperText="Click on the map to set location"
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                  if (isEdit) setOpenEditDialog(false);
                  else setOpenAddDialog(false);
                }}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              >
                {isEdit ? 'Update Business' : 'Add Business'}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
          Business Management
        </Typography>
        {renderContent()}
        {renderFormDialog(false)}
        {renderFormDialog(true)}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDeleteCompany}
              loading={loading}
              color="error"
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Alerts
          snackbar={snackbar}
          handleClose={handleCloseSnackbar}
          darkMode={darkMode}
        />
      </Box>
    </Box>
  );
};

export default AddCompany;